import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { concat, fn } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { Section } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import { eq } from 'tio-ui/utilities';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import {
  PhRobot,
  PhPaperPlaneRight,
  PhSparkle,
  PhUser,
  PhLightbulb,
  PhTarget,
  PhPath,
  PhBriefcase,
  PhGraduationCap,
  PhChartLine,
  PhHeart,
  PhMicrophone,
  PhPaperclip,
} from 'ember-phosphor-icons';
import type { CareerPlan, EmployeeCareerProfile } from 'tio-employee/services/career-pathways-api';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';

interface CareerPathwaysAdvisorSignature {
  Args: {
    model: {
      employee: EmployeeCareerProfile;
      careerPlan: CareerPlan;
    };
  };
}

interface ChatMessage {
  id: number;
  role: 'assistant' | 'user';
  content: string;
  timestamp: string;
}

const buildInitialMessage = (employee: EmployeeCareerProfile, careerPlan: CareerPlan) => {
  const targetRole = careerPlan.targetRoleTitle || 'your next role';
  const progress = Math.round(careerPlan.progress);

  const message: ChatMessage = {
    id: 1,
    role: 'assistant',
    content: `Hi ${employee.firstName}! 👋 I'm your AI Career Advisor. I'm here to help you navigate your career journey at ${employee.currentRole.department}.

I can see you're working toward becoming a **${targetRole}** — that's an exciting goal! Based on your current progress, you're ${progress}% of the way there.

What would you like to explore today?`,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  };

  return message;
};

const AI_RESPONSES_BY_CLIENT: Record<string, Record<string, string>> = {
  advocate: {
    skills: `Based on your skills profile and goal of becoming a Nurse Manager, I'd recommend focusing on these areas:

**1. Leadership & Team Management** (Critical Gap) 🔴
- Recommended: Nurse Leadership Certificate (ANCC)
- TA eligible: Yes ($1,850)
- Timeline: 4 months

**2. Budget & Resource Management** (In Progress) 🟡
- You're working on the Healthcare Finance module
- Essential for unit-level management

**3. Quality Improvement & Metrics** (Future Focus) 🔵
- Consider Lean Six Sigma Healthcare certification

Would you like me to help you create a development plan for any of these areas?`,

    progress: `Great question! Let me analyze your progress toward Nurse Manager:

📊 **Overall Progress: 68%**

**What you've accomplished:**
✅ BSN Degree (Completed via TA)
✅ 5+ years RN experience
✅ Charge Nurse experience (2 years)
🔄 Nurse Leadership Certificate (65% complete)

**Timeline Assessment:**
- Current projection: **On track** ✨

Based on your current pace, you should be ready for Nurse Manager by your target date. Want me to help optimize your development plan?`,

    opportunities: `Here's what's happening at your hospital that aligns with your career direction:

🔥 **High Demand Roles:**

**1. Charge Nurse**
- Great stepping stone to leadership

**2. Nurse Manager**
- Your target role!

**3. Clinical Nurse Educator**
- Alternative leadership path

Would you like to explore any of these opportunities in detail?`,

    learning: `Here are my top certification recommendations for Nurse Manager:

🏆 **Best Match:**
**Nurse Leadership Certificate (ANCC)**
- Duration: 4 months (online)
- Cost: $1,850 (TA Eligible ✅)

📚 **Alternative Options:**
**Charge Nurse Training Program** (Free - Internal)
**Healthcare Management Certificate**

Should I add this to your career plan?`,
  },
  ups: {
    skills: `Based on your profile and goal of becoming a Hub Supervisor, I'd recommend focusing on these areas:

**1. Leadership & Team Coaching** (Critical Gap) 🔴
- Recommended: Supervisor Leadership Program
- TA eligible: Yes

**2. Scheduling & Staffing** (In Progress) 🟡
- Essential for running a successful shift

**3. Process Improvement** (Future Focus) 🔵
- Consider Lean Operations fundamentals

Want help building a development plan for any of these areas?`,

    progress: `Here's your progress toward Hub Supervisor:

📊 **Overall Progress: 62%**

**What you've accomplished:**
✅ 3+ years operations experience
✅ Safety Leadership training
🔄 OSHA Compliance certificate (45% complete)

**Timeline Assessment:**
- Current projection: **On track** ✨

Want me to help optimize your plan for promotion readiness?`,

    opportunities: `Here's what's happening across your hubs:

🔥 **High Demand Roles:**

**1. Shift Supervisor**
- Great stepping stone

**2. Hub Supervisor**
- Your target role

**3. Operations Manager**
- Longer-term leadership track

Interested in any of these openings?`,

    learning: `Here are my top recommendations for Hub Supervisor:

🏆 **Best Match:**
**Supervisor Leadership Program**
- Duration: 10 weeks
- Cost: Covered

📚 **Alternative Options:**
**Lean Operations Certificate**
**Safety & Compliance Track**

Should I add these to your plan?`,
  },
  starbucks: {
    skills: `Based on your profile and goal of becoming a Store Manager, I'd recommend focusing on these areas:

**1. Labor Planning & Scheduling** (Critical Gap) 🔴
- Recommended: Store Manager Development

**2. Financial Management** (In Progress) 🟡
- Key to store performance goals

**3. Coaching & Talent Development** (Future Focus) 🔵
- Builds partner engagement and retention

Want help building a development plan for any of these areas?`,

    progress: `Here's your progress toward Store Manager:

📊 **Overall Progress: 70%**

**What you've accomplished:**
✅ 3+ years retail experience
✅ Shift Supervisor experience
🔄 Store Manager Development (55% complete)

**Timeline Assessment:**
- Current projection: **On track** ✨

Want me to help optimize your plan for promotion readiness?`,

    opportunities: `Here's what's happening in your district:

🔥 **High Demand Roles:**

**1. Shift Supervisor**
- Great stepping stone

**2. Store Manager**
- Your target role

**3. District Operations Lead**
- Longer-term leadership track

Interested in any of these openings?`,

    learning: `Here are my top recommendations for Store Manager:

🏆 **Best Match:**
**Store Manager Launch**
- Duration: 12 months
- Cost: Covered

📚 **Alternative Options:**
**Retail Operations Certificate**
**Coffee Leadership Track**

Should I add these to your plan?`,
  },
};

export default class CareerPathwaysAdvisor extends Component<CareerPathwaysAdvisorSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked messages: ChatMessage[] = [
    buildInitialMessage(this.args.model.employee, this.args.model.careerPlan),
  ];
  @tracked inputValue = '';
  @tracked isTyping = false;

  get employee() {
    return this.args.model.employee;
  }

  get careerPlan() {
    return this.args.model.careerPlan;
  }

  get progressPercent() {
    return Math.round(this.careerPlan.progress);
  }

  get taAvailable() {
    const clientId = getCurrentDemoClientData().client.id;

    if (clientId === 'ups') {
      return '$3,400 remaining';
    }

    if (clientId === 'starbucks') {
      return '$4,100 remaining';
    }

    return '$2,910 remaining';
  }

  get suggestedPrompts() {
    const targetRole = this.args.model.careerPlan.targetRoleTitle || 'your next role';

    return [
      {
        icon: PhTarget,
        text: 'What leadership skills should I develop?',
        category: 'skills',
      },
      {
        icon: PhPath,
        text: `Am I on track for ${targetRole}?`,
        category: 'progress',
      },
      {
        icon: PhBriefcase,
        text: 'What leadership roles are open?',
        category: 'opportunities',
      },
      {
        icon: PhGraduationCap,
        text: 'What certifications help my advancement?',
        category: 'learning',
      },
    ];
  }

  get aiResponses() {
    const clientId = getCurrentDemoClientData().client.id as keyof typeof AI_RESPONSES_BY_CLIENT;
    return AI_RESPONSES_BY_CLIENT[clientId] ?? AI_RESPONSES_BY_CLIENT.advocate!;
  }

  sendMessage = () => {
    if (!this.inputValue.trim()) return;

    const userMessage = {
      id: this.messages.length + 1,
      role: 'user' as const,
      content: this.inputValue,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    this.messages = [...this.messages, userMessage];
    const query = this.inputValue.toLowerCase();
    this.inputValue = '';
    this.isTyping = true;

    // Simulate AI thinking
    setTimeout(() => {
      let response = this.aiResponses.skills ?? ''; // default
      const targetRole = this.args.model.careerPlan.targetRoleTitle?.toLowerCase();

      if (
        query.includes('track') ||
        query.includes('timeline') ||
        query.includes('progress') ||
        (targetRole && query.includes(targetRole))
      ) {
        response = this.aiResponses.progress ?? '';
      } else if (
        query.includes('role') ||
        query.includes('demand') ||
        query.includes('opening') ||
        query.includes('leadership')
      ) {
        response = this.aiResponses.opportunities ?? '';
      } else if (
        query.includes('certification') ||
        query.includes('course') ||
        query.includes('learn') ||
        query.includes('advancement')
      ) {
        response = this.aiResponses.learning ?? '';
      }

      const aiMessage = {
        id: this.messages.length + 1,
        role: 'assistant' as const,
        content: response || 'I can help you with that! Let me look into it.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      this.messages = [...this.messages, aiMessage];
      this.isTyping = false;
    }, 1500);
  };

  handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  };

  useSuggestedPrompt = (text: string) => {
    this.inputValue = text;
    this.sendMessage();
  };

  updateInput = (event: Event) => {
    this.inputValue = (event.target as HTMLTextAreaElement).value;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <DataIndicator @source="placeholder" @showBadge={{true}} @dataSourceId="ai-recommendations">
      <div class="data-audit w-full min-w-0 h-[calc(100vh-220px)] tio-col-extra-loose lg:flex-row">
        {{! Main Chat Area }}
        <div
          class="tio-col-flush flex-1 tio-bg-surface rounded-lg shadow-sm overflow-hidden min-w-0"
        >
          {{! Chat Header }}
          <div class="tio-gradient-brand tio-fg-inverse p-4 tio-row-loose">
            <div class="p-3 bg-white/20 rounded-full">
              <PhRobot @size={{28}} />
            </div>
            <div>
              <Heading @level={{2}} @size="lg" class="tio-fg-inverse">AI Career Advisor</Heading>
              <Text @size="sm" class="text-violet-200">
                Powered by AI • Personalized to your goals
              </Text>
            </div>
            <div class="ml-auto tio-row-tight">
              <span class="w-2 h-2 tio-bg-success rounded-full animate-pulse"></span>
              <Text @size="sm" class="text-violet-200">Online</Text>
            </div>
          </div>

          {{! Messages Area }}
          <div class="flex-1 overflow-y-auto p-6 space-y-6 tio-bg-sunken">
            {{#each this.messages as |message|}}
              <div class="flex gap-4 {{if (eq message.role 'user') 'flex-row-reverse'}}">
                {{! Avatar }}
                <div
                  class="shrink-0 w-10 h-10 rounded-full tio-row-center
                    {{if (eq message.role 'assistant') 'tio-bg-brand-soft' 'tio-bg-info-soft'}}"
                >
                  {{#if (eq message.role "assistant")}}
                    <PhRobot @size={{20}} class="tio-fg-brand" />
                  {{else}}
                    <PhUser @size={{20}} class="tio-fg-info" />
                  {{/if}}
                </div>

                {{! Message Bubble }}
                <div
                  class="max-w-[80%] rounded-2xl p-4 shadow-sm
                    {{if
                      (eq message.role 'assistant')
                      'tio-bg-surface rounded-tl-none'
                      'tio-bg-info text-white rounded-tr-none'
                    }}"
                >
                  <div
                    class="prose prose-sm max-w-none {{if (eq message.role 'user') 'prose-invert'}}"
                  >
                    {{! Simple markdown rendering - in real app use a proper parser }}
                    <div class="whitespace-pre-wrap">{{message.content}}</div>
                  </div>
                  <Text
                    @variant="muted"
                    @size="xs"
                    class="mt-2 {{if (eq message.role 'user') 'text-teal-200' ''}}"
                  >
                    {{message.timestamp}}
                  </Text>
                </div>
              </div>
            {{/each}}

            {{! Typing Indicator }}
            {{#if this.isTyping}}
              <div class="tio-flex-loose">
                <div class="shrink-0 w-10 h-10 rounded-full tio-bg-brand-soft tio-row-center">
                  <PhRobot @size={{20}} class="tio-fg-brand" />
                </div>
                <div class="tio-bg-surface rounded-2xl rounded-tl-none p-4 shadow-sm">
                  <div class="tio-flex-extra-tight">
                    <span
                      class="w-2 h-2 tio-bg-brand rounded-full animate-bounce animation-delay-0"
                    ></span>
                    <span
                      class="w-2 h-2 tio-bg-brand rounded-full animate-bounce animation-delay-150"
                    ></span>
                    <span
                      class="w-2 h-2 tio-bg-brand rounded-full animate-bounce animation-delay-300"
                    ></span>
                  </div>
                </div>
              </div>
            {{/if}}
          </div>

          {{! Input Area }}
          <div class="p-4 tio-bg-surface border-t tio-border-default">
            {{! Suggested Prompts }}
            {{#if (eq this.messages.length 1)}}
              <div class="mb-4">
                <Text @variant="muted" @size="sm" class="mb-2">Quick questions:</Text>
                <div class="tio-wrap-tight">
                  {{#each this.suggestedPrompts as |prompt|}}
                    <button
                      type="button"
                      {{on "click" (fn this.useSuggestedPrompt prompt.text)}}
                      class="tio-row-tight px-3 py-2 tio-bg-brand-wash tio-fg-brand rounded-full text-sm hover:tio-bg-brand-soft-hover transition-colors"
                    >
                      {{#let prompt.icon as |Icon|}}
                        {{! @glint-ignore - dynamic icon }}
                        <Icon @size={{16}} />
                      {{/let}}
                      {{prompt.text}}
                    </button>
                  {{/each}}
                </div>
              </div>
            {{/if}}

            {{! Text Input }}
            <div class="flex gap-3 items-end">
              <div class="flex-1 relative">
                <textarea
                  value={{this.inputValue}}
                  placeholder="Ask me anything about your career..."
                  rows="2"
                  {{on "input" this.updateInput}}
                  {{on "keypress" this.handleKeyPress}}
                  class="w-full px-4 py-3 pr-24 border tio-border-strong rounded-xl resize-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                ></textarea>
                <div class="tio-flex-extra-tight absolute right-2 bottom-2">
                  <button
                    type="button"
                    class="p-2 tio-fg-disabled hover:tio-fg-secondary rounded-lg hover:tio-bg-sunken"
                  >
                    <PhPaperclip @size={{20}} />
                  </button>
                  <button
                    type="button"
                    class="p-2 tio-fg-disabled hover:tio-fg-secondary rounded-lg hover:tio-bg-sunken"
                  >
                    <PhMicrophone @size={{20}} />
                  </button>
                </div>
              </div>
              <Button @intent="primary" {{on "click" this.sendMessage}} disabled={{this.isTyping}}>
                <PhPaperPlaneRight @size={{20}} />
              </Button>
            </div>
            <Text @variant="muted" @size="xs" class="mt-2 text-center">
              AI responses are suggestions. Always verify important decisions with your manager or
              HR.
            </Text>
          </div>
        </div>

        {{! Sidebar - Context Panel }}
        <div class="w-80 space-y-4">
          {{! Your Context }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <div class="tio-row-tight">
                <PhUser @size={{18}} class="tio-fg-brand" />
                <span>Your Context</span>
              </div>
            </:header>
            <:body>
              <div class="space-y-3 text-sm">
                <div>
                  <Text @variant="muted" @size="xs">Current Role</Text>
                  <div class="font-medium">{{this.employee.currentRole.title}}</div>
                </div>
                <div>
                  <Text @variant="muted" @size="xs">Career Goal</Text>
                  <div class="font-medium tio-fg-brand">
                    {{this.careerPlan.targetRoleTitle}}
                  </div>
                </div>
                <div>
                  <Text @variant="muted" @size="xs">Progress</Text>
                  <div class="tio-row-tight">
                    <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        class="h-full tio-bg-brand-accent rounded-full"
                        style={{htmlSafe (concat "width: " this.progressPercent "%")}}
                      ></div>
                    </div>
                    <span class="font-medium">{{this.progressPercent}}%</span>
                  </div>
                </div>
                <div>
                  <Text @variant="muted" @size="xs">TA Available</Text>
                  <div class="font-medium tio-fg-success">{{this.taAvailable}}</div>
                </div>
              </div>
            </:body>
          </Section>

          {{! Conversation Topics }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <div class="tio-row-tight">
                <PhLightbulb @size={{18}} class="tio-fg-warning" />
                <span>I can help with</span>
              </div>
            </:header>
            <:body>
              <div class="space-y-2">
                <div class="tio-row-tight text-sm tio-fg-secondary">
                  <PhTarget @size={{16}} class="tio-fg-brand-accent" />
                  <span>Skills gap analysis</span>
                </div>
                <div class="tio-row-tight text-sm tio-fg-secondary">
                  <PhPath @size={{16}} class="tio-fg-brand-accent" />
                  <span>Career path exploration</span>
                </div>
                <div class="tio-row-tight text-sm tio-fg-secondary">
                  <PhGraduationCap @size={{16}} class="tio-fg-brand-accent" />
                  <span>Course recommendations</span>
                </div>
                <div class="tio-row-tight text-sm tio-fg-secondary">
                  <PhBriefcase @size={{16}} class="tio-fg-brand-accent" />
                  <span>Internal opportunities</span>
                </div>
                <div class="tio-row-tight text-sm tio-fg-secondary">
                  <PhChartLine @size={{16}} class="tio-fg-brand-accent" />
                  <span>Salary insights</span>
                </div>
                <div class="tio-row-tight text-sm tio-fg-secondary">
                  <PhHeart @size={{16}} class="tio-fg-brand-accent" />
                  <span>Work-life balance</span>
                </div>
              </div>
            </:body>
          </Section>

          {{! Privacy Note }}
          <div class="tio-bg-brand-wash rounded-lg p-4 border tio-border-brand-soft">
            <div class="tio-row-top-tight">
              <PhSparkle @size={{18}} class="tio-fg-brand-accent mt-0.5" />
              <div>
                <Text @size="sm" @weight="medium" class="tio-fg-brand">Your privacy matters</Text>
                <Text @variant="muted" @size="xs" class="mt-1">
                  Conversations are not shared with your manager. We use your profile data to give
                  personalized advice.
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DataIndicator>
  </template>
}
